import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import SearchableSelect from "../components/SearchableSelect";
import { PageHeader, Button, Select } from "../components/ui";
import { errMsg } from "../lib/utils";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import * as XLSX from "xlsx";

const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const num = (v) => (Math.round(v || 0) === 0 ? "-" : Math.round(v).toLocaleString("en-US"));
const monthName = (ym) => BULAN[Number(ym.slice(5, 7)) - 1];

function NumLink({ row, bucket, customerId }) {
  const v = row[bucket];
  if (Math.round(v || 0) === 0) return "-";
  const qs = new URLSearchParams({ period_month: row.period_month, bucket, ...(customerId ? { customer_id: customerId } : {}) });
  return (
    <Link to={`/pipeline/detail?${qs}`} target="_blank" rel="noopener" data-testid={`pipeline-link-${bucket}-${row.period_month}`}
      className="hover:underline hover:text-[#1f3864] decoration-dotted underline-offset-2" title="Buka detail AWB di tab baru">
      {num(v)}
    </Link>
  );
}

function StatusCell({ row, onSaved }) {
  const [val, setVal] = useState(row.status || "");
  useEffect(() => setVal(row.status || ""), [row.status]);
  const save = () => {
    if (val === (row.status || "")) return;
    api.put("/reports/receivable-pipeline/notes", { period_month: row.period_month, status: val })
      .then(() => { toast.success(`Status ${monthName(row.period_month)} disimpan`); onSaved(row.period_month, val); })
      .catch((e) => toast.error(errMsg(e)));
  };
  return (
    <input
      data-testid={`pipeline-status-${row.period_month}`}
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      placeholder="…"
      className="w-full bg-transparent px-1 py-0.5 outline-none focus:bg-white focus:ring-1 focus:ring-[#1f3864] placeholder:text-transparent hover:placeholder:text-slate-400"
    />
  );
}

export default function ReceivablePipeline() {
  const nowY = new Date().getFullYear();
  const [year, setYear] = useState(nowY);
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/reports/receivable-pipeline/monthly", { params: { year, ...(customerId ? { customer_id: customerId } : {}) } })
      .then((r) => { setRows(r.data.rows || []); setCustomers(r.data.customers || []); })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [year, customerId]);

  const onSaved = (ym, status) => setRows((rs) => rs.map((r) => (r.period_month === ym ? { ...r, status } : r)));

  const exportXLS = () => {
    const ws = XLSX.utils.json_to_sheet(rows.map((r) => ({
      Bulan: monthName(r.period_month), Orders: Math.round(r.orders), "NY PO": Math.round(r.ny_po),
      Outstanding: Math.round(r.outstanding), Paid: Math.round(r.paid), Status: r.status || "",
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Pipeline ${year}`);
    XLSX.writeFile(wb, `Pipeline_Penerimaan_${year}${customerId ? "_" + (customers.find((c) => c.id === customerId)?.name || "") : ""}.xlsx`);
  };

  const th = "bg-[#1f3864] text-white font-bold px-3 py-1.5 text-center border-r border-white/60 last:border-r-0";
  const td = "px-3 py-1 border-r border-white whitespace-nowrap";

  return (
    <div data-testid="receivable-pipeline-page">
      <PageHeader title="Pipeline Penerimaan" />
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="w-32">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Tahun (tgl order)</div>
          <Select data-testid="pipeline-year-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[nowY + 1, nowY, nowY - 1, nowY - 2].map((y) => <option key={y} value={y}>{y}</option>)}
          </Select>
        </div>
        <div className="w-80">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Customer</div>
          <SearchableSelect
            testid="pipeline-customer-filter"
            value={customerId}
            onChange={(v) => setCustomerId(v || "")}
            options={customers.map((c) => ({ value: c.id, label: c.name, sub: `Order ${year}: ${num(c.total)}` }))}
            placeholder="Semua Customer"
            emptyText="Customer tidak ditemukan"
          />
        </div>
        <Button data-testid="pipeline-export-btn" variant="secondary" onClick={exportXLS}><Download size={14}/> Export Excel</Button>
        {loading && <span className="text-xs text-slate-400 inline-flex items-center gap-1 pb-2"><Loader2 size={13} className="animate-spin"/> memuat…</span>}
      </div>

      <div className="overflow-x-auto">
        <table data-testid="pipeline-monthly-table" className="text-[13px] text-slate-900 border-collapse min-w-[760px]" style={{ fontFamily: "Calibri, 'Segoe UI', Arial, sans-serif" }}>
          <thead>
            <tr>
              <th className={`${th} w-28`}>Bulan</th>
              <th className={`${th} w-36`}>Orders</th>
              <th className={`${th} w-36`}>NY PO</th>
              <th className={`${th} w-36`}>Outstanding</th>
              <th className={`${th} w-36`}>Paid</th>
              <th className={`${th} w-64`}>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.period_month} data-testid={`pipeline-row-${r.period_month}`} className={i % 2 === 0 ? "bg-[#ddebf7]" : "bg-white"}>
                <td className={`${td} text-left`}>{monthName(r.period_month)}</td>
                <td className={`${td} text-right`} data-testid={`pipeline-orders-${r.period_month}`}><NumLink row={r} bucket="orders" customerId={customerId}/></td>
                <td className={`${td} text-right`}><NumLink row={r} bucket="ny_po" customerId={customerId}/></td>
                <td className={`${td} text-right`}><NumLink row={r} bucket="outstanding" customerId={customerId}/></td>
                <td className={`${td} text-right`}><NumLink row={r} bucket="paid" customerId={customerId}/></td>
                <td className={`${td} text-left p-0`}><StatusCell row={r} onSaved={onSaved}/></td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="text-center text-slate-400 py-6">Tidak ada data di tahun ini.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-500 mt-3 max-w-3xl">
        Dikelompokkan per bulan tanggal order. Orders = NY PO (Open, Reconciled & invoice belum diprint) + Outstanding (invoice sudah diprint, belum dibayar) + Paid.
        Klik angka untuk membuka daftar AWB-nya di tab baru. Kolom Status bisa diketik langsung — tersimpan otomatis (berlaku untuk semua customer).
      </p>
    </div>
  );
}
